import {
  filterUpgradeOffersForFamilyMode,
  sortOffersByTierDesc,
} from 'utils/marketing-offers';
import resolvePlanCardDescription from 'utils/plan-tier-taglines-en';
import type { IAvailableOffer } from 'services/plan/types';
import type { IOfferPlan } from 'services/offer/types';

export const PLAN_DETAILS_PAGE_SIZE = 3;

export type PlanGroup = {
  plan: IOfferPlan;
  monthly: IAvailableOffer | null;
  annual: IAvailableOffer | null;
};

export type PlanCardData = {
  planCode: string;
  planName: string;
  planTier: string;
  featureCodes: string[];
  description: string;
  monthlyOfferCode: string | null;
  monthlyPrice: number | null;
  annualOfferCode: string | null;
  annualPrice: number | null;
  isCurrent: boolean;
  recommended: boolean;
  isComplimentary?: boolean;
};

export type BillingTermName = 'MONTHLY' | 'ANNUAL';

export function isComplimentaryBillingType(
  billingType: string | null | undefined,
): boolean {
  return billingType?.toLowerCase() === 'complimentary';
}

export function normalizeUserBillingTerm(term: string): BillingTermName {
  return term.toUpperCase() === 'ANNUAL' ? 'ANNUAL' : 'MONTHLY';
}

export function mergeOffersToGroup(
  offers: IAvailableOffer[],
): PlanGroup | null {
  if (!offers.length) return null;
  const { plan } = offers[0];
  let monthly: IAvailableOffer | null = null;
  let annual: IAvailableOffer | null = null;
  offers.forEach((avail) => {
    const t = avail.offer.subscriptionPlan?.termName?.toUpperCase();
    if (t === 'ANNUAL') annual = avail;
    else monthly = avail;
  });
  return { plan, monthly, annual };
}

export function buildUpgradePlanCards(
  pool: IAvailableOffer[],
  recommendedPlanCode: string,
): PlanCardData[] {
  const sorted = sortOffersByTierDesc(pool);
  const seen = new Set<string>();
  const planOrder = sorted.reduce<string[]>((acc, o) => {
    if (!seen.has(o.plan.code)) {
      seen.add(o.plan.code);
      acc.push(o.plan.code);
    }
    return acc;
  }, []);

  return planOrder.flatMap((code) => {
    const subset = pool.filter((o) => o.plan.code === code);
    const g = mergeOffersToGroup(subset);
    if (!g) return [];
    return [
      {
        planCode: g.plan.code,
        planName: g.plan.name,
        planTier: g.plan.tier ?? '',
        featureCodes: [...(g.plan.features ?? [])],
        description: resolvePlanCardDescription(g.plan.tier, g.plan.name),
        monthlyOfferCode: g.monthly?.offer.code ?? null,
        monthlyPrice: g.monthly?.offer.price ?? null,
        annualOfferCode: g.annual?.offer.code ?? null,
        annualPrice: g.annual?.offer.price ?? null,
        isCurrent: false,
        recommended: code === recommendedPlanCode,
      },
    ];
  });
}

export function buildFamilyAndIndividualUpgradePools(
  validOffers: IAvailableOffer[],
  upgradeFilterBase: {
    currentPlanCode: string | undefined;
    currentBillingType: string | null | undefined;
    currentPrice: number;
  },
) {
  const famUpgrades = filterUpgradeOffersForFamilyMode(validOffers, {
    ...upgradeFilterBase,
    wantFamily: true,
  });
  const indUpgrades = filterUpgradeOffersForFamilyMode(validOffers, {
    ...upgradeFilterBase,
    wantFamily: false,
  });
  return { famUpgrades, indUpgrades };
}
